import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Gamepad2, Home, History, Menu, X } from 'lucide-react'
import { useState } from 'react'
import ThemeToggle from '../features/ThemeToggle'
import Button from '../ui/Button'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  
  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/tic-tac-toe', label: 'Tic Tac Toe', icon: Gamepad2 },
    { path: '/2048', label: '2048', icon: Gamepad2 },
  ]
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }
  
  const itemVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  }
  
  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2
      }
    },
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3
      }
    }
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-background text-text transition-theme">
      {/* Header */}
      <motion.header 
        className="sticky top-0 z-50 border-b border-border bg-surface/80 backdrop-blur-sm"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center gap-3 group">
                <motion.div 
                  className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center"
                  whileHover={{ rotate: 15, scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  <Gamepad2 className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-xl font-bold group-hover:text-primary transition-colors">
                    Basic Website Games
                  </h1>
                  <p className="text-sm text-text-muted">
                    Play classic games in your browser
                  </p>
                </div>
              </Link>
            </motion.div>
            
            {/* Desktop Navigation */}
            <motion.nav 
              className="hidden md:flex items-center gap-6"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {navItems.map((item) => {
                const isActive = location.pathname === item.path
                return (
                  <motion.div key={item.path} variants={itemVariants}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors relative ${
                        isActive 
                          ? 'text-primary bg-primary/10' 
                          : 'hover:bg-surface'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                      {isActive && (
                        <motion.div
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"
                          layoutId="navbar-indicator"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                )
              })}
            </motion.nav>
            
            {/* Theme Toggle and Actions */}
            <motion.div 
              className="flex items-center gap-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                icon={History}
                as={Link}
                to="/"
                className="hidden sm:flex"
              >
                <span className="hidden sm:inline">History</span>
              </Button>
              
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                icon={mobileMenuOpen ? X : Menu}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden"
              >
                Menu
              </Button>
            </motion.div>
          </div>
          
          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                variants={mobileMenuVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="md:hidden overflow-hidden"
              >
                <div className="flex flex-col gap-2 mt-4 pb-4">
                  {navItems.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                      <motion.div
                        key={item.path}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 100 }}
                      >
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isActive 
                              ? 'text-primary bg-primary/10' 
                              : 'hover:bg-surface'
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                          {isActive && (
                            <motion.div
                              className="ml-auto w-2 h-2 rounded-full bg-primary"
                              layoutId="mobile-indicator"
                            />
                          )}
                        </Link>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>
      
      {/* Main Content */}
      <motion.main 
        className="flex-1 container mx-auto px-4 py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        key={location.pathname}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </motion.main>
      
      {/* Footer */}
      <motion.footer 
        className="border-t border-border bg-surface py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-lg font-bold mb-2">Basic Website Games</h3>
              <p className="text-text-muted max-w-md">
                A collection of classic games built with React 19, TypeScript, and modern web technologies.
                All game data is stored locally in your browser.
              </p>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-text-muted hover:text-primary transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              <p className="text-sm text-text-muted">
                © {new Date().getFullYear()} Basic Website Games. All game logic implemented locally.
              </p>
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  )
}